import type { ParticleType, Particle } from "./FactionGrid";
import styles from "../faction.module.css";

type FactionKey = "angel" | "demon" | "mortal" | "security" | "ronin";

type Props = {
  faction: FactionKey;
  particles: Record<ParticleType, Particle[]> | null;
};

function ParticleHost({ type, particles }: { type: ParticleType; particles: Record<ParticleType, Particle[]> | null }) {
  if (!particles) return null;
  return (
    <>
      {particles[type].map((p) => (
        <span key={p.key} className={p.className} style={p.style}>
          {p.content}
        </span>
      ))}
    </>
  );
}

// The prototype's fixed-count decorative pieces (security shards, ronin
// slashes) are hand-placed in markup with their own inline custom
// properties, not JS-spawned — ported verbatim rather than folded into the
// particle system.
export default function FactionFX({ faction, particles }: Props) {
  if (faction === "angel") {
    return (
      <div className={styles.fxLayer} aria-hidden="true">
        <div className={styles.angelRays} />
        <ParticleHost type="feather" particles={particles} />
        <div className={styles.identityVignette}>
          <div className={styles.angelSigil} />
          <ParticleHost type="angelStar" particles={particles} />
        </div>
      </div>
    );
  }

  if (faction === "demon") {
    return (
      <div className={styles.fxLayer} aria-hidden="true">
        <div className={styles.demonHeat} />
        <ParticleHost type="ember" particles={particles} />
        <div className={styles.identityVignette}>
          <div className={styles.demonSmoke} />
          <div className={styles.demonCracks} />
          <div className={styles.demonScratch} />
        </div>
      </div>
    );
  }

  if (faction === "mortal") {
    return (
      <div className={styles.fxLayer} aria-hidden="true">
        <div className={styles.mortalRing} />
        <ParticleHost type="petal" particles={particles} />
        <div className={styles.identityVignette}>
          <div className={styles.mortalChi} />
          <div className={styles.mortalWave} />
          <ParticleHost type="leaf" particles={particles} />
        </div>
      </div>
    );
  }

  if (faction === "security") {
    return (
      <div className={styles.fxLayer} aria-hidden="true">
        <div className={`${styles.securityRing} ${styles.securityRingR1}`} />
        <div className={`${styles.securityRing} ${styles.securityRingR2}`} />
        <div className={styles.securityScan} />
        <div className={styles.securityGlitch} />
        <div className={styles.identityVignette}>
          <div className={styles.securityData} />
          <ParticleHost type="dataBit" particles={particles} />
          <span className={styles.securityShard} style={{ left: "18%", top: "25%" }} />
          <span className={styles.securityShard} style={{ right: "13%", top: "39%", animationDelay: "-2s", transform: "rotate(-24deg)" }} />
          <span className={styles.securityShard} style={{ left: "31%", bottom: "21%", animationDelay: "-4s", transform: "rotate(8deg)" }} />
        </div>
      </div>
    );
  }

  return (
    <div className={styles.fxLayer} aria-hidden="true">
      <div className={styles.roninMist} />
      <span className={styles.roninSlash} style={{ ["--top" as string]: "38%", ["--rot" as string]: "-18deg", ["--delay" as string]: "0s" }} />
      <span className={styles.roninSlash} style={{ ["--top" as string]: "53%", ["--rot" as string]: "14deg", ["--delay" as string]: "2.7s" }} />
      <div className={styles.identityVignette}>
        <div className={styles.roninMoonBloom} />
        <ParticleHost type="ash" particles={particles} />
        <div className={styles.roninIai} />
      </div>
    </div>
  );
}
